import { Chart, ChartDataset, Plugin } from "chart.js";

interface ILineChartDataset extends ChartDataset<"line"> {
  parameterName?: string;
  idPoint?: number;
}

interface IHtmlLegendOptions {
  containerId: string;
  fontFamily: string;
  syncChartRefs?: Array<{ current: Chart | null }>;
  onDatasetVisibilityChange?: (seriesKey: string, visible: boolean) => void;
}

const getSeriesKey = (idPoint?: number, parameterName?: string): string | null => {
  if (idPoint == null || !parameterName) {
    return null;
  }

  return `${idPoint}:${parameterName}`;
};

const findDatasetIndexBySeriesKey = (chart: Chart, seriesKey: string): number => {
  const datasets = chart.data.datasets as ILineChartDataset[];

  return datasets.findIndex(dataset => {
    const datasetSeriesKey = getSeriesKey(dataset.idPoint, dataset.parameterName);

    return datasetSeriesKey === seriesKey;
  });
};

const getHtmlLegendOptions = (chart: Chart): IHtmlLegendOptions | undefined =>
  (chart.options.plugins as { htmlLegend?: IHtmlLegendOptions } | undefined)?.htmlLegend;

const createElement = (tag: string, styles: Partial<CSSStyleDeclaration> = {}, text?: string) => {
  const el = document.createElement(tag);
  Object.assign(el.style, styles);
  if (text) el.textContent = text;
  return el;
};

const clearContainer = (container: HTMLElement) => {
  while (container.firstChild) {
    container.firstChild.remove();
  }
};

const createLegendItem = (item: any, chart: any, fontFamily: string): HTMLElement => {
  const li = createElement("li", {
    alignItems: "center",
    cursor: "pointer",
    display: "flex",
    marginRight: "12px",
    marginTop: "8px",
    fontFamily,
    fontSize: "12px",
    listStyle: "none",
    flexDirection: "row",
    textDecoration: item.hidden ? "line-through" : "none",
    opacity: item.hidden ? "0.5" : "1",
  });

  li.onclick = () => {
    const datasetIndex = item.datasetIndex as number;
    const pairedDatasetIndex = item.pairedDatasetIndex as number | undefined;

    const isVisible = chart.isDatasetVisible(datasetIndex);
    const nextVisible = !isVisible;

    chart.setDatasetVisibility(datasetIndex, nextVisible);

    if (pairedDatasetIndex !== undefined) {
      chart.setDatasetVisibility(pairedDatasetIndex, nextVisible);
    }

    const seriesKey = getSeriesKey(item.idPoint, item.parameterName);
    const htmlLegendOptions = getHtmlLegendOptions(chart);

    if (seriesKey) {
      htmlLegendOptions?.onDatasetVisibilityChange?.(seriesKey, nextVisible);
    }

    const syncRefs = htmlLegendOptions?.syncChartRefs ?? [];

    for (const ref of syncRefs) {
      const syncChart = ref?.current;

      if (!syncChart || !seriesKey) {
        continue;
      }

      const syncDatasetIndex = findDatasetIndexBySeriesKey(syncChart, seriesKey);

      if (syncDatasetIndex === -1) {
        continue;
      }

      syncChart.setDatasetVisibility(syncDatasetIndex, nextVisible);
      syncChart.update();
    }
    chart.update();
  };

  const box = createElement("span", {
    background: item.strokeStyle ?? item.fillStyle,
    borderColor: item.strokeStyle ?? "",
    borderWidth: `${item.lineWidth ?? 1}px`,
    borderRadius: `${(item.borderRadius as number) || 50}px`,
    display: "inline-block",
    height: "12px",
    marginRight: "8px",
    width: "12px",
    flexShrink: "0",
    alignSelf: "center",
  });

  li.appendChild(box);

  if (item.pairedFillStyle) {
    const box2 = createElement("span", {
      background: item.pairedFillStyle,
      opacity: "0.5",
      borderRadius: "50px",
      display: "inline-block",
      height: "12px",
      marginRight: "8px",
      width: "12px",
      flexShrink: "0",
      alignSelf: "center",
    });
    li.appendChild(box2);
  }

  const textContainer = createElement("span", {
    color: item.fontColor,
    fontFamily,
    fontSize: "12px",
    margin: "0",
    padding: "0",
    textDecoration: item.hidden ? "line-through" : "",
  });
  textContainer.appendChild(document.createTextNode(item.text));
  li.appendChild(textContainer);

  return li;
};

const renderGroup = (ul: HTMLElement, title: string, items: any[], chart: any, fontFamily: string) => {
  const groupRow = createElement("div", {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: "8px",
    marginLeft: "8px",
    flexWrap: "wrap",
  });

  const groupTitle = createElement(
    "span",
    {
      fontWeight: "bold",
      fontFamily,
      fontSize: "13px",
      marginTop: "8px",
      marginRight: "16px",
      whiteSpace: "nowrap",
    },
    title + ":",
  );

  const groupContainer = createElement("div", {
    display: "flex",
    flexWrap: "wrap",
    marginLeft: "8px",
  });

  groupRow.appendChild(groupTitle);
  groupRow.appendChild(groupContainer);
  ul.appendChild(groupRow);

  items.forEach(item => {
    const li = createLegendItem(item, chart, fontFamily);
    groupContainer.appendChild(li);
  });
};

export const getOrCreateLegendList = (id: string) => {
  if (!id) return null;

  const legendContainer = document.getElementById(id);
  if (!legendContainer) return null;

  let listContainer = legendContainer.querySelector("ul");

  if (!listContainer) {
    listContainer = createElement("ul", {
      display: "flex",
      flexDirection: "column",
      margin: "0",
      padding: "4px",
    }) as HTMLUListElement;
    legendContainer.appendChild(listContainer);
  }

  return listContainer;
};

export const HtmlLegendPlugin: Plugin = {
  id: "htmlLegend",
  afterUpdate: (chart, _args, options: IHtmlLegendOptions) => {
    const ul = getOrCreateLegendList(options.containerId);
    if (!ul) return;

    const generateLabels = chart.options.plugins?.legend?.labels?.generateLabels;
    if (!generateLabels) return;
    const items = generateLabels(chart);

    const newState = JSON.stringify({
      chartId: chart.id,
      items: items.map(i => ({
        hidden: i.hidden,
        text: i.text,
        datasetIndex: i.datasetIndex,
        yAxisID: (i as any).yAxisID,
      })),
    });
    if ((ul as any).__legendState === newState) return;
    (ul as any).__legendState = newState;

    clearContainer(ul);
    const chartType = (chart.config as any).type;

    if (chartType === "radar") {
      const grouped: Record<string, any[]> = {};
      items?.forEach(item => {
        const parameterName = (item as any).parameterName;
        if (!grouped[parameterName]) grouped[parameterName] = [];
        grouped[parameterName].push(item);
      });

      Object.entries(grouped).forEach(([param, group]) => {
        renderGroup(ul, param, group, chart, options.fontFamily);
      });
    } else if (chartType === "line") {
      const grouped: Record<string, any[]> = {};
      items?.forEach(item => {
        const customYAxisID = (item as any).yAxisID as string | undefined;
        const dataset = chart.data.datasets[item.datasetIndex!] as ILineChartDataset;
        const yAxisID = customYAxisID ?? (dataset.yAxisID as string);
        if (!grouped[yAxisID]) grouped[yAxisID] = [];
        grouped[yAxisID].push(item);
      });

      Object.entries(grouped).forEach(([axisID, group]) => {
        renderGroup(ul, axisID, group, chart, options.fontFamily);
      });
    }
  },
};
